import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
// StoryStrip only appears above the fold on the homepage; defer its module
// so it never blocks initial paint of Search + Available Properties.
const StoryStrip = lazy(() => import("@/components/StoryStrip"));
import { Search, SlidersHorizontal, X, ClipboardList, ArrowRight, ShieldCheck } from "lucide-react";
import { PROPERTY_TYPES, TRANSACTION_TYPES, MANGALORE_LOCATIONS, AREA_UNITS } from "@/lib/constants";
import { toSqft, formatArea } from "@/lib/format";

const initialFilters = {
  q: "",
  transaction_type: "all",
  property_type: "all",
  location: "all",
  min_price: "",
  max_price: "",
  min_area: "",
  max_area: "",
  area_unit: "sqft",
};

// SessionStorage cache for the property list — instant back-navigation
// (SWR pattern: show cached list immediately, revalidate in background).
const HOME_LIST_KEY = (params) => `home_list_${JSON.stringify(params)}`;
const readHomeCache = (params) => {
  try {
    const raw = sessionStorage.getItem(HOME_LIST_KEY(params));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeHomeCache = (params, data) => {
  try { sessionStorage.setItem(HOME_LIST_KEY(params), JSON.stringify(data)); } catch {}
};

export default function HomePage() {
  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters are visible by default on all viewports so mobile users see them
  // clearly right under the search bar (no hidden/collapsed frame).
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    let ignore = false;
    const params = { only_approved: true };
    const { min_area, max_area, area_unit, ...rest } = applied;
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== "" && v !== "all" && v !== null && v !== undefined) params[k] = v;
    });
    // Convert user-entered area (in chosen unit) to sq.ft for backend
    if (min_area) params.min_area = toSqft(min_area, area_unit);
    if (max_area) params.max_area = toSqft(max_area, area_unit);

    // Hydrate from cache first so the grid appears instantly on return.
    const cached = readHomeCache(params);
    if (cached) {
      setItems(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    api.get("/properties", { params })
      .then(({ data }) => {
        if (ignore) return;
        setItems(data || []);
        writeHomeCache(params, data || []);
      })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied]);

  const active = useMemo(() => {
    const list = [];
    Object.entries(applied).forEach(([k, v]) => {
      if (k === "area_unit") return; // never a "chip"
      if (v === "" || v === "all" || v === initialFilters[k]) return;
      list.push([k, v]);
    });
    return list;
  }, [applied]);

  const submit = (e) => { e?.preventDefault(); setApplied({ ...filters }); setShowFilters(false); };
  const clearAll = () => { setFilters(initialFilters); setApplied(initialFilters); };

  return (
    <div className="space-y-5">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl mpn-gradient-hero text-white p-5 sm:p-8">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,.4), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.25), transparent 40%)" }} />
        <div className="relative">
          <div className="text-[11px] tracking-widest uppercase opacity-90">Buy · Sell · Rent · Lease</div>
          <h1 className="mt-1 text-2xl sm:text-4xl font-display font-extrabold leading-tight">
            Find your next property deal
          </h1>
          <p className="mt-2 max-w-xl text-white/90 text-sm sm:text-base">
            Owners, sellers, buyers and agents — post listings, share requirements, discover matches.
          </p>

          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2 bg-white rounded-2xl p-2 shadow-lg">
            <div className="flex items-center flex-1 px-3">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                data-testid="search-input"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search by title, locality or Property ID (e.g. AD-P-00001)"
                className="w-full h-11 pl-2 text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
            <button
              type="button"
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters((s) => !s)}
              className="mpn-btn-ghost h-11 text-sm sm:min-w-[130px]"
            >
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
            <button type="submit" data-testid="search-submit-btn" className="mpn-btn-primary h-11">
              Search
            </button>
          </form>
        </div>
      </section>

      {/* Filters panel — sits directly below Search so mobile users
          see it without any extra tap; also moved above the listings
          so results below always reflect the active filter set. */}
      {showFilters && (
        <section className="mpn-card p-4 sm:p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="mpn-label">Sale / Rent</label>
              <select
                data-testid="filter-transaction"
                value={filters.transaction_type}
                onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
                className="mpn-input"
              >
                <option value="all">Any</option>
                {TRANSACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mpn-label">Property Type</label>
              <select
                data-testid="filter-property-type"
                value={filters.property_type}
                onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
                className="mpn-input"
              >
                <option value="all">Any type</option>
                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="mpn-label">Location</label>
              <select
                data-testid="filter-location"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="mpn-input"
              >
                <option value="all">All India</option>
                {MANGALORE_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="mpn-label">Min Price (₹)</label>
              <input data-testid="filter-min-price" type="number" min="0" className="mpn-input"
                     value={filters.min_price}
                     onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} />
            </div>
            <div>
              <label className="mpn-label">Max Price (₹)</label>
              <input data-testid="filter-max-price" type="number" min="0" className="mpn-input"
                     value={filters.max_price}
                     onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} />
            </div>
            <div className="col-span-2 md:col-span-4">
              <label className="mpn-label">Property Area {filters.min_area || filters.max_area ? <span className="text-xs text-muted-foreground font-normal">(≈ {filters.min_area ? formatArea(toSqft(filters.min_area, filters.area_unit), "sqft") : "0"} – {filters.max_area ? formatArea(toSqft(filters.max_area, filters.area_unit), "sqft") : "any"})</span> : null}</label>
              <div className="grid grid-cols-5 gap-2">
                <input data-testid="filter-min-area" type="number" min="0" step="0.01" className="mpn-input col-span-2"
                       placeholder="Min"
                       value={filters.min_area}
                       onChange={(e) => setFilters({ ...filters, min_area: e.target.value })} />
                <input data-testid="filter-max-area" type="number" min="0" step="0.01" className="mpn-input col-span-2"
                       placeholder="Max"
                       value={filters.max_area}
                       onChange={(e) => setFilters({ ...filters, max_area: e.target.value })} />
                <select data-testid="filter-area-unit" className="mpn-input"
                        value={filters.area_unit}
                        onChange={(e) => setFilters({ ...filters, area_unit: e.target.value })}>
                  {AREA_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Enter any range and unit — the search converts across acres, cents, sq.ft and sq.m automatically.</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <button data-testid="filters-clear-btn" onClick={clearAll} className="text-sm text-muted-foreground hover:text-foreground">
              Clear all
            </button>
            <button data-testid="filters-apply-btn" onClick={submit} className="mpn-btn-primary">Apply filters</button>
          </div>
        </section>
      )}

      {active.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {active.map(([k, v]) => (
            <span key={k} className="mpn-chip bg-accent text-accent-foreground border border-emerald-200">
              {k.replace("_", " ")}: {String(v)}
              <button
                data-testid={`clear-filter-${k}`}
                onClick={() => { const next = { ...applied, [k]: initialFilters[k] }; setApplied(next); setFilters(next); }}
                className="ml-1 hover:opacity-70"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Property Stories — Instagram-style strip at the top of results,
          directly under Search/Filters and just before the property grid.
          Wrapped in Suspense so listings paint even before the strip resolves. */}
      <Suspense fallback={null}>
        <StoryStrip />
      </Suspense>

      {/* Listing grid */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="mpn-section-title">Available Properties</h2>
          <span data-testid="results-count" className="text-sm text-muted-foreground">{items.length} result{items.length === 1 ? "" : "s"}</span>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 max-w-xl md:max-w-none mx-auto">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border overflow-hidden animate-pulse">
                <div className="flex items-center gap-2.5 px-3 py-2.5">
                  <div className="w-9 h-9 rounded-full bg-muted" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-2 bg-muted rounded w-1/3" />
                  </div>
                </div>
                <div className="aspect-[4/5] sm:aspect-[16/11] bg-muted" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="empty-state" className="mpn-card p-8 text-center">
            <p className="font-medium">No properties match your filters</p>
            <p className="text-sm text-muted-foreground mt-1">Try widening the search — clear filters and browse everything.</p>
            <button onClick={clearAll} className="mpn-btn-ghost mt-4">Clear filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 max-w-xl md:max-w-none mx-auto">
            {items.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>

      {/* Post Your Requirement CTA — placed below the results so browsers
          scan the listings first, then fall back to posting a requirement. */}
      <Link
        to="/post-requirement"
        data-testid="home-post-requirement-cta"
        className="mpn-card block p-5 sm:p-6 border-l-4 border-secondary hover:shadow-md"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-secondary grid place-items-center text-white shrink-0">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-secondary">
              <ShieldCheck className="w-3.5 h-3.5" /> 100% Private
            </div>
            <h3 className="font-display font-extrabold text-base sm:text-lg mt-0.5 leading-tight">
              Can't find what you need? Post Your Requirement
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Tell us what you need — our team hand-picks matches and calls you. Never shown on the public site.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-secondary shrink-0" />
        </div>
      </Link>
    </div>
  );
}
